import { useState, useEffect, useMemo, useRef } from 'react';
import { getConnectorConfig, saveConnectorConfigs } from '../utils/connectorConfig';
import {
  Database,
  FolderOpen,
  Folder,
  File,
  RefreshCw,
  Search,
  X,
  Share2,
  CheckCircle,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  HardDrive,
  Globe,
  Loader2,
  FileJson,
  FileText,
  Download,
  Eye,
  Home,
  Settings,
  Save,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Shield,
  Clock,
  Calendar,
  Users,
  Plus,
  Trash2
} from 'lucide-react';
import apiClient from '../services/api';

export default function DataStore() {
  const [buckets, setBuckets] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [objects, setObjects] = useState([]);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bucketsLoading, setBucketsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const [totalObjectCount, setTotalObjectCount] = useState(0);
  const [currentPrefix, setCurrentPrefix] = useState('');

  // Server-side pagination
  const [serverPage, setServerPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [serverFolders, setServerFolders] = useState([]);
  const itemsPerPage = 100;

  // Sorting
  const [sortBy, setSortBy] = useState('lastModified'); // 'name', 'size', 'lastModified'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  // Ref for scrolling table on page change
  const tableRef = useRef(null);

  // EDC Configuration
  const [showConfig, setShowConfig] = useState(false);
  const [providerUrl, setProviderUrl] = useState(
    getConnectorConfig('providerEdcUrl')
  );
  const [apiKey, setApiKey] = useState(
    getConnectorConfig('providerApiKey')
  );

  // Asset publishing form
  const [assetForm, setAssetForm] = useState({
    assetId: '',
    name: '',
    description: '',
    contentType: 'application/json',
    policyId: ''
  });

  // Policy restrictions
  const [policyRestrictions, setPolicyRestrictions] = useState([]);
  const [showAddRestriction, setShowAddRestriction] = useState(false);

  useEffect(() => {
    fetchBuckets();
  }, []);

  useEffect(() => {
    if (selectedBucket) {
      fetchObjects(selectedBucket, 1, '', '');
      setSearchQuery('');
      setCurrentPrefix('');
      setServerPage(1);
    }
  }, [selectedBucket]);

  // Debounced server-side search
  useEffect(() => {
    if (!selectedBucket) return;
    const delay = searchQuery ? 400 : 0;
    const timeoutId = setTimeout(() => {
      fetchObjects(selectedBucket, 1, searchQuery, currentPrefix);
    }, delay);
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const fetchBuckets = async () => {
    try {
      setBucketsLoading(true);
      const response = await apiClient.get('/objects/buckets');
      setBuckets(response.data.buckets || []);
    } catch (error) {
      console.error('Failed to fetch buckets:', error);
      setBuckets([
        { name: 'forecasts', creationDate: null },
        { name: 'telemetry-data', creationDate: null },
        { name: 'telemetry-forecasts', creationDate: null },
        { name: 'weather-data', creationDate: null },
        { name: 'enact-objects', creationDate: null }
      ]);
    } finally {
      setBucketsLoading(false);
    }
  };

  const fetchObjects = async (bucketName, page = 1, search = '', prefix = '', overrideSort = null) => {
    try {
      setLoading(true);
      setObjects([]);

      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (prefix) params.set('prefix', prefix);
      params.set('page', page);
      params.set('pageSize', itemsPerPage);
      params.set('sortBy', overrideSort?.sortBy ?? sortBy);
      params.set('sortOrder', overrideSort?.sortOrder ?? sortOrder);

      const response = await apiClient.get(`/objects/bucket/${bucketName}?${params}`);
      const rawObjects = response.data.objects || [];
      const seen = new Set();
      const uniqueObjects = rawObjects.filter(obj => {
        if (seen.has(obj.name)) return false;
        seen.add(obj.name);
        return true;
      });
      setObjects(uniqueObjects);
      setServerFolders(response.data.folders || []);
      setTotalObjectCount(response.data.totalCount || 0);
      setTotalPages(response.data.totalPages || 1);
      setServerPage(page);
      if (prefix !== undefined) setCurrentPrefix(prefix);
    } catch (error) {
      console.error('Failed to fetch objects:', error);
      setObjects([]);
      setServerFolders([]);
      setTotalObjectCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleBucketClick = (bucketName) => {
    setSelectedBucket(bucketName);
    setSelectedObjects([]);
    setSearchQuery('');
    setCurrentPrefix('');
    setServerPage(1);
  };

  const handleObjectSelect = (object) => {
    setSelectedObjects(prev => {
      const isSelected = prev.some(o => o.name === object.name);
      if (isSelected) {
        return prev.filter(o => o.name !== object.name);
      } else {
        return [...prev, object];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedObjects.length === paginatedFiles.length) {
      setSelectedObjects([]);
    } else {
      setSelectedObjects([...paginatedFiles]);
    }
  };

  const openPublishModal = () => {
    if (selectedObjects.length === 0) return;

    const firstObject = selectedObjects[0];
    const baseName = firstObject.name.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '-');

    setAssetForm({
      assetId: `asset-${baseName}-${Date.now()}`,
      name: selectedObjects.length === 1
        ? firstObject.name
        : `${selectedBucket} collection (${selectedObjects.length} objects)`,
      description: `Data from MinIO bucket: ${selectedBucket}`,
      contentType: firstObject.name.endsWith('.json') ? 'application/json' : 'application/octet-stream',
      policyId: ''
    });
    setPolicyRestrictions([]);
    setShowAddRestriction(false);
    setPublishResult(null);
    setShowPublishModal(true);
  };

  // Add a new policy restriction
  const addRestriction = (type) => {
    const newRestriction = {
      id: Date.now(),
      type,
      // Default values based on type
      ...(type === 'participantId' && { participantId: '' }),
      ...(type === 'timeRestriction' && { dateTime: '' }),
      ...(type === 'timespanRestriction' && { fromDate: '', toDate: '' }),
    };
    setPolicyRestrictions([...policyRestrictions, newRestriction]);
    setShowAddRestriction(false);
  };

  // Remove a policy restriction
  const removeRestriction = (id) => {
    setPolicyRestrictions(policyRestrictions.filter(r => r.id !== id));
  };

  // Update a restriction field
  const updateRestriction = (id, field, value) => {
    setPolicyRestrictions(policyRestrictions.map(r =>
      r.id === id ? { ...r, [field]: value } : r
    ));
  };

  const handlePublishToEDC = async () => {
    setPublishLoading(true);
    setPublishResult(null);

    try {
      const results = [];

      // Build policy constraints from restrictions
      const constraints = policyRestrictions.map(restriction => {
        if (restriction.type === 'participantId' && restriction.participantId) {
          return {
            type: 'participantId',
            leftOperand: 'participantId',
            operator: 'eq',
            rightOperand: restriction.participantId
          };
        }
        if (restriction.type === 'timeRestriction' && restriction.dateTime) {
          return {
            type: 'dateTime',
            leftOperand: 'dateTime',
            operator: 'lteq',
            rightOperand: new Date(restriction.dateTime).toISOString()
          };
        }
        if (restriction.type === 'timespanRestriction' && restriction.fromDate && restriction.toDate) {
          return {
            type: 'timespan',
            fromDate: new Date(restriction.fromDate).toISOString(),
            toDate: new Date(restriction.toDate).toISOString()
          };
        }
        return null;
      }).filter(Boolean);

      for (const obj of selectedObjects) {
        const assetId = selectedObjects.length === 1
          ? assetForm.assetId
          : `${assetForm.assetId}-${obj.name.replace(/[^a-zA-Z0-9-_]/g, '-')}`;

        // Build MinIO proxy URL for EDC data address
        const objectPath = `${selectedBucket}/${obj.name}`;
        const encodedPath = btoa(objectPath);
        const baseUrl = window.location.origin;
        const dataUrl = `${baseUrl}/api/v1/data-exchange/minio-proxy/${encodedPath}`;

        await apiClient.post('/data-exchange/provider/asset', {
          providerUrl: providerUrl || undefined,
          apiKey: apiKey || undefined,
          assetId,
          assetData: {
            name: selectedObjects.length === 1 ? assetForm.name : obj.name,
            description: assetForm.description,
            contentType: assetForm.contentType,
            dataUrl
          },
          // Include policy restrictions
          policyConstraints: constraints.length > 0 ? constraints : undefined
        });

        results.push({
          objectName: obj.name,
          assetId,
          dataUrl,
          restrictions: constraints.length,
          success: true
        });
      }

      setPublishResult({
        success: true,
        message: `Successfully published ${results.length} asset(s) to EDC${constraints.length > 0 ? ` with ${constraints.length} restriction(s)` : ''}`,
        assets: results
      });

      setSelectedObjects([]);

    } catch (error) {
      console.error('Failed to publish to EDC:', error);
      setPublishResult({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to publish to EDC'
      });
    } finally {
      setPublishLoading(false);
    }
  };

  const saveConfiguration = () => {
    saveConnectorConfigs({ providerEdcUrl: providerUrl, providerApiKey: apiKey });
    setShowConfig(false);
  };

  const getFileIcon = (filename) => {
    if (filename.endsWith('.json')) return <FileJson className="text-yellow-400" size={18} />;
    if (filename.endsWith('.txt') || filename.endsWith('.csv')) return <FileText className="text-blue-400" size={18} />;
    return <File className="text-gray-400" size={18} />;
  };

  const formatSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const time = d.toLocaleTimeString();
    return `${day}/${month}/${year}, ${time}`;
  };

  // Handle sorting - triggers server-side re-fetch
  const handleSort = (column) => {
    const newOrder = sortBy === column ? (sortOrder === 'asc' ? 'desc' : 'asc') : 'desc';
    setSortBy(column);
    setSortOrder(newOrder);
    if (selectedBucket) {
      fetchObjects(selectedBucket, 1, searchQuery, currentPrefix, { sortBy: column, sortOrder: newOrder });
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown size={14} className="text-gray-500" />;
    return sortOrder === 'asc'
      ? <ArrowUp size={14} className="text-blue-400" />
      : <ArrowDown size={14} className="text-blue-400" />;
  };

  // Folders come from server; files are the current page returned by server
  const folders = serverFolders;

  // Add displayName to server-returned files (already sorted and paginated)
  const sortedFiles = useMemo(() => {
    return objects.map(obj => ({
      ...obj,
      displayName: currentPrefix ? obj.name.slice(currentPrefix.length) : obj.name
    }));
  }, [objects, currentPrefix]);

  const paginatedFiles = sortedFiles; // Server already paginated

  // Navigate into a folder
  const handleFolderClick = (folderName) => {
    const newPrefix = currentPrefix + folderName + '/';
    setSearchQuery('');
    fetchObjects(selectedBucket, 1, '', newPrefix);
  };

  // Get breadcrumb parts
  const breadcrumbs = currentPrefix ? currentPrefix.slice(0, -1).split('/') : [];

  // Navigate to a specific breadcrumb level
  const handleBreadcrumbClick = (index) => {
    if (index < 0) {
      setSearchQuery('');
      fetchObjects(selectedBucket, 1, '', '');
    } else {
      const newPrefix = breadcrumbs.slice(0, index + 1).join('/') + '/';
      setSearchQuery('');
      fetchObjects(selectedBucket, 1, '', newPrefix);
    }
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (serverPage > 3) pages.push('...');

      const start = Math.max(2, serverPage - 1);
      const end = Math.min(totalPages - 1, serverPage + 1);

      for (let i = start; i <= end; i++) pages.push(i);

      if (serverPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-display text-white mb-2">
            Data Store
          </h1>
          <p className="text-gray-400">
            Browse MinIO storage and publish data to EDC connectors
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowConfig(!showConfig)}
            className="btn btn-secondary"
          >
            <Settings size={20} />
            EDC Config
          </button>
          <button
            onClick={fetchBuckets}
            className="btn btn-primary"
            disabled={bucketsLoading}
          >
            <RefreshCw size={20} className={bucketsLoading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* EDC Configuration Panel */}
      {showConfig && (
        <div className="card border-blue-500/30">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings size={20} className="text-blue-400" />
            Provider EDC Configuration
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Provider Management API URL (optional - uses default if empty)
              </label>
              <input
                type="text"
                value={providerUrl}
                onChange={(e) => setProviderUrl(e.target.value)}
                
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                API Key (optional)
              </label>
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder=""
                className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-3">
              <button onClick={saveConfiguration} className="btn btn-primary">
                <Save size={20} />
                Save Configuration
              </button>
              <button onClick={() => setShowConfig(false)} className="btn btn-secondary">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Buckets List */}
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <HardDrive className="text-blue-400" size={20} />
              Buckets
            </h2>

            {bucketsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-blue-400" size={32} />
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto pr-2 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#4b5563 transparent'
                }}>
                {buckets.map((bucket) => (
                  <button
                    key={bucket.name}
                    onClick={() => handleBucketClick(bucket.name)}
                    className={`w-full flex items-center gap-3 p-2 rounded-lg transition-all text-left ${
                      selectedBucket === bucket.name
                        ? 'bg-blue-600/20 border border-blue-500/50'
                        : 'bg-gray-800/50 border border-gray-700 hover:border-blue-500/30'
                    }`}
                  >
                    <FolderOpen
                      size={18}
                      className={selectedBucket === bucket.name ? 'text-blue-400' : 'text-yellow-400'}
                    />
                    <span className="text-sm font-medium truncate">{bucket.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Objects List */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Database className="text-green-400" size={20} />
                {selectedBucket ? (
                  <>Objects in <span className="text-blue-400">{selectedBucket}</span></>
                ) : (
                  'Select a bucket'
                )}
                {selectedBucket && !loading && (
                  <span className="text-sm text-gray-400 font-normal">
                    ({totalObjectCount} files{folders.length > 0 ? `, ${folders.length} folders` : ''})
                  </span>
                )}
              </h2>

              {selectedObjects.length > 0 && (
                <button
                  onClick={openPublishModal}
                  className="btn btn-primary"
                >
                  <Share2 size={18} />
                  Publish to EDC ({selectedObjects.length})
                </button>
              )}
            </div>

            {selectedBucket && (
              <>
                {/* Breadcrumb Navigation */}
                {currentPrefix && (
                  <div className="flex items-center gap-2 mb-4 text-sm">
                    <button
                      onClick={() => handleBreadcrumbClick(-1)}
                      className="text-blue-400 hover:text-blue-300 flex items-center gap-1"
                    >
                      <Home size={14} />
                      {selectedBucket}
                    </button>
                    {breadcrumbs.map((crumb, index) => (
                      <span key={index} className="flex items-center gap-2">
                        <ChevronRight size={14} className="text-gray-500" />
                        <button
                          onClick={() => handleBreadcrumbClick(index)}
                          className={index === breadcrumbs.length - 1
                            ? 'text-gray-300'
                            : 'text-blue-400 hover:text-blue-300'}
                        >
                          {crumb}
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Search and Select All */}
                <div className="flex gap-4 mb-4">
                  <div className="relative flex-1">
                    {loading && searchQuery ? (
                      <Loader2 className="absolute left-3 top-1/2 transform -translate-y-1/2 text-blue-400 animate-spin" size={18} />
                    ) : (
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                    )}
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                      }}
                      placeholder="Search all objects..."
                      className="w-full pl-10 pr-10 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>

                  {paginatedFiles.length > 0 && (
                    <button
                      onClick={handleSelectAll}
                      className="btn btn-secondary text-sm"
                    >
                      {selectedObjects.length === paginatedFiles.length ? 'Deselect All' : 'Select All'}
                    </button>
                  )}
                </div>

                {/* Objects Table */}
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="animate-spin text-blue-400" size={48} />
                  </div>
                ) : (folders.length === 0 && sortedFiles.length === 0) ? (
                  <div className="text-center py-12 text-gray-400">
                    <Database className="mx-auto mb-3 opacity-50" size={48} />
                    <p>{searchQuery ? `No objects matching "${searchQuery}"` : 'No objects in this folder'}</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto" ref={tableRef}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-700 text-left text-gray-400">
                            <th className="pb-3 pr-4 w-10"></th>
                            <th className="pb-3 pr-4">
                              <button
                                onClick={() => handleSort('name')}
                                className="flex items-center gap-2 hover:text-white transition-colors"
                              >
                                Name {getSortIcon('name')}
                              </button>
                            </th>
                            <th className="pb-3 pr-4">
                              <button
                                onClick={() => handleSort('size')}
                                className="flex items-center gap-2 hover:text-white transition-colors"
                              >
                                Size {getSortIcon('size')}
                              </button>
                            </th>
                            <th className="pb-3 pr-4">
                              <button
                                onClick={() => handleSort('lastModified')}
                                className="flex items-center gap-2 hover:text-white transition-colors"
                              >
                                Last Modified {getSortIcon('lastModified')}
                              </button>
                            </th>
                            <th className="pb-3">Actions</th>
                          </tr>
                        </thead>
                      </table>
                      <div className="max-h-[440px] overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: '#4b5563 transparent' }}>
                        <table className="w-full text-sm">
                          <tbody>
                            {/* Render folders first (only on page 1) */}
                            {serverPage === 1 && folders.map((folderName) => (
                              <tr
                                key={`folder-${folderName}`}
                                className="border-b border-gray-800 hover:bg-gray-800/30 cursor-pointer"
                                onClick={() => handleFolderClick(folderName)}
                              >
                                <td className="py-3 pr-4 w-10"></td>
                                <td className="py-3 pr-4">
                                  <div className="flex items-center gap-2">
                                    <Folder size={18} className="text-yellow-400" />
                                    <span className="text-white font-medium">{folderName}/</span>
                                  </div>
                                </td>
                                <td className="py-3 pr-4 text-gray-500">—</td>
                                <td className="py-3 pr-4 text-gray-500">—</td>
                                <td className="py-3 text-gray-500">
                                  <ChevronRight size={16} />
                                </td>
                              </tr>
                            ))}
                            {/* Render files */}
                            {paginatedFiles.map((obj) => {
                              const isSelected = selectedObjects.some(o => o.name === obj.name);
                              return (
                                <tr
                                  key={obj.name}
                                  className={`border-b border-gray-800 hover:bg-gray-800/30 cursor-pointer ${
                                    isSelected ? 'bg-blue-900/20' : ''
                                  }`}
                                  onClick={() => handleObjectSelect(obj)}
                                >
                                  <td className="py-3 pr-4 w-10">
                                    <input

                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() => handleObjectSelect(obj)}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded-full border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
                                    />
                                  </td>
                                  <td className="py-3 pr-4">
                                    <div className="flex items-center gap-2">
                                      {getFileIcon(obj.displayName || obj.name)}
                                      <span className="text-white truncate max-w-md">{obj.displayName || obj.name}</span>
                                    </div>
                                  </td>
                                  <td className="py-3 pr-4 text-gray-400">{formatSize(obj.size)}</td>
                                  <td className="py-3 pr-4 text-gray-400">{formatDate(obj.lastModified)}</td>
                                  <td className="py-3">
                                    <div className="flex items-center gap-1">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const path = btoa(`${selectedBucket}/${obj.name}`);
                                          window.open(`/api/v1/data-exchange/minio-proxy/${path}`, '_blank');
                                        }}
                                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                                        title="View"
                                      >
                                        <Eye size={16} className="text-gray-400 hover:text-blue-400" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          const path = btoa(`${selectedBucket}/${obj.name}`);
                                          window.open(`/api/v1/data-exchange/minio-proxy/${path}?download=true`, '_blank');
                                        }}
                                        className="p-1 hover:bg-gray-700 rounded transition-colors"
                                        title="Download"
                                      >
                                        <Download size={16} className="text-gray-400 hover:text-green-400" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Server-side Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between pt-4 border-t border-gray-800 mt-4">
                        <div className="text-sm text-gray-400">
                          Showing {(serverPage - 1) * itemsPerPage + 1}–{Math.min(serverPage * itemsPerPage, totalObjectCount)} of {totalObjectCount} files
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => fetchObjects(selectedBucket, Math.max(1, serverPage - 1), searchQuery, currentPrefix)}
                            disabled={serverPage === 1}
                            className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft size={16} />
                          </button>

                          {getPageNumbers().map((page, idx) => (
                            page === '...' ? (
                              <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">...</span>
                            ) : (
                              <button
                                key={page}
                                onClick={() => fetchObjects(selectedBucket, page, searchQuery, currentPrefix)}
                                className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${
                                  serverPage === page
                                    ? 'bg-blue-600 text-white'
                                    : 'hover:bg-gray-700 text-gray-400'
                                }`}
                              >
                                {page}
                              </button>
                            )
                          ))}

                          <button
                            onClick={() => fetchObjects(selectedBucket, Math.min(totalPages, serverPage + 1), searchQuery, currentPrefix)}
                            disabled={serverPage === totalPages}
                            className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronRight size={16} />
                          </button>
                        </div>
                      </div>
                    )}

                  </>
                )}
              </>
            )}

            {!selectedBucket && (
              <div className="text-center py-12 text-gray-400">
                <FolderOpen className="mx-auto mb-3 opacity-50" size={48} />
                <p>Select a bucket from the left to browse objects</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Publish Modal */}
      {showPublishModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowPublishModal(false)}>
          <div className="bg-gray-900 rounded-lg p-6 max-w-lg w-full mx-4 border border-gray-700" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Share2 size={24} className="text-blue-400" />
                <div>
                  <h3 className="text-lg font-semibold text-white">Publish to EDC</h3>
                  <p className="text-sm text-gray-400">Create EDC asset(s) from MinIO data</p>
                </div>
              </div>
              <button onClick={() => setShowPublishModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Selected Objects Summary */}
            <div className="bg-gray-800/50 rounded-lg p-3 mb-4">
              <p className="text-sm text-gray-400 mb-2">Selected objects ({selectedObjects.length}):</p>
              <div className="max-h-24 overflow-y-auto space-y-1">
                {selectedObjects.map(obj => (
                  <div key={obj.name} className="flex items-center gap-2 text-sm">
                    {getFileIcon(obj.name)}
                    <span className="text-gray-300 truncate">{obj.name}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Asset Form */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Asset ID</label>
                <input
                  type="text"
                  value={assetForm.assetId}
                  onChange={(e) => setAssetForm({...assetForm, assetId: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Display Name</label>
                <input
                  type="text"
                  value={assetForm.name}
                  onChange={(e) => setAssetForm({...assetForm, name: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={assetForm.description}
                  onChange={(e) => setAssetForm({...assetForm, description: e.target.value})}
                  rows={2}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Content Type</label>
                <select
                  value={assetForm.contentType}
                  onChange={(e) => setAssetForm({...assetForm, contentType: e.target.value})}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="application/json">application/json</option>
                  <option value="text/csv">text/csv</option>
                  <option value="text/plain">text/plain</option>
                  <option value="application/xml">application/xml</option>
                  <option value="application/octet-stream">application/octet-stream</option>
                </select>
              </div>

              {/* Publishing Restrictions */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                    <Shield size={16} className="text-blue-400" />
                    Publishing Restrictions
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAddRestriction(!showAddRestriction)}
                      className="text-xs px-3 py-1 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 rounded-lg flex items-center gap-1 transition-colors"
                    >
                      <Plus size={14} />
                      Add Restriction
                    </button>
                    {showAddRestriction && (
                      <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 min-w-48">
                        <button
                          onClick={() => addRestriction('participantId')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 rounded-t-lg"
                        >
                          <Users size={14} className="text-purple-400" />
                          Consumer's Participant ID
                        </button>
                        <button
                          onClick={() => addRestriction('timeRestriction')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Clock size={14} className="text-yellow-400" />
                          Time Restriction
                        </button>
                        <button
                          onClick={() => addRestriction('timespanRestriction')}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-700 flex items-center gap-2 rounded-b-lg"
                        >
                          <Calendar size={14} className="text-green-400" />
                          Timespan Restriction
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {policyRestrictions.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No restrictions - data offer will be public</p>
                ) : (
                  <div className="space-y-3">
                    {policyRestrictions.map((restriction) => (
                      <div key={restriction.id} className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium flex items-center gap-2">
                            {restriction.type === 'participantId' && (
                              <><Users size={12} className="text-purple-400" /> Consumer Participant ID</>
                            )}
                            {restriction.type === 'timeRestriction' && (
                              <><Clock size={12} className="text-yellow-400" /> Time Restriction</>
                            )}
                            {restriction.type === 'timespanRestriction' && (
                              <><Calendar size={12} className="text-green-400" /> Timespan Restriction</>
                            )}
                          </span>
                          <button
                            onClick={() => removeRestriction(restriction.id)}
                            className="text-gray-500 hover:text-red-400 transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>

                        {restriction.type === 'participantId' && (
                          <input
                            type="text"
                            value={restriction.participantId || ''}
                            onChange={(e) => updateRestriction(restriction.id, 'participantId', e.target.value)}
                            
                            className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-blue-500"
                          />
                        )}

                        {restriction.type === 'timeRestriction' && (
                          <input
                            type="datetime-local"
                            value={restriction.dateTime || ''}
                            onChange={(e) => updateRestriction(restriction.id, 'dateTime', e.target.value)}
                            className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-blue-500"
                          />
                        )}

                        {restriction.type === 'timespanRestriction' && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">From</label>
                              <input
                                type="datetime-local"
                                value={restriction.fromDate || ''}
                                onChange={(e) => updateRestriction(restriction.id, 'fromDate', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">To</label>
                              <input
                                type="datetime-local"
                                value={restriction.toDate || ''}
                                onChange={(e) => updateRestriction(restriction.id, 'toDate', e.target.value)}
                                className="w-full px-3 py-1.5 text-sm bg-gray-900 border border-gray-600 rounded focus:ring-1 focus:ring-blue-500"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Result Message */}
            {publishResult && (
              <div className={`mt-4 p-3 rounded-lg ${
                publishResult.success ? 'bg-green-900/20 border border-green-800' : 'bg-red-900/20 border border-red-800'
              }`}>
                <div className="flex gap-2">
                  {publishResult.success ? (
                    <CheckCircle className="text-green-400 flex-shrink-0" size={20} />
                  ) : (
                    <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
                  )}
                  <div>
                    <p className={publishResult.success ? 'text-green-400' : 'text-red-400'}>
                      {publishResult.message}
                    </p>
                    {publishResult.assets && (
                      <div className="mt-2 text-xs text-gray-400">
                        {publishResult.assets.map((asset, idx) => (
                          <div key={idx}>Asset: {asset.assetId}</div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePublishToEDC}
                disabled={publishLoading}
                className="btn btn-primary flex-1"
              >
                {publishLoading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <Globe size={18} />
                )}
                {publishLoading ? 'Publishing...' : 'Publish to EDC'}
              </button>
              <button
                onClick={() => setShowPublishModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
